// Lightweight read-only renderer for BlockNote JSON content.
//
// The whole point of this file: we don't want to ship the full BlockNote editor bundle
// (~600KB gzipped) on the read path. Most of the time users are reading, not editing.
// This renderer handles the common block types directly. The full editor is lazy-loaded
// only when the user clicks "Edit" (Phase 6).
//
// Supported block types: paragraph, heading (1/2/3), bulletListItem, numberedListItem,
// checkListItem, quote, codeBlock, image, video, audio, file, table, columnList/column,
// bookmark, embed. Unknown blocks render as a fallback div with the type name.

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type TreeNodeResponse } from '../../lib/api.js';
import { PageIcon } from '../PageIcon.js';

interface InlineContent {
  type: string;
  text?: string;
  styles?: Record<string, boolean | string>;
  href?: string;
  content?: InlineContent[];
  /** Custom inline content props (e.g. pageLink stores its target id here). */
  props?: Record<string, unknown>;
}

interface BlockNode {
  id?: string;
  type: string;
  props?: Record<string, unknown>;
  content?: InlineContent[] | BlockNode[];
  children?: BlockNode[];
}

interface BlockRendererProps {
  content: unknown;
}

export function BlockRenderer({ content }: BlockRendererProps) {
  // Pull the tree from the TanStack Query cache so we can resolve internal link
  // targets to their real icons. Cached across the whole session — no extra fetch.
  const { data: tree } = useQuery({
    queryKey: ['tree'],
    queryFn: () => api.getTree(),
  });

  const pageById = useMemo(() => {
    const m = new Map<string, TreeNodeResponse>();
    if (tree) for (const p of tree.pages) m.set(p.id, p);
    return m;
  }, [tree]);

  if (!content) return null;
  // Some BlockNote schemas wrap blocks in { type: "doc", content: [...] }, others
  // are just an array. Normalize.
  let blocks: BlockNode[];
  if (Array.isArray(content)) {
    blocks = content as BlockNode[];
  } else if (typeof content === 'object' && content !== null && 'content' in content) {
    blocks = ((content as { content: BlockNode[] }).content ?? []) as BlockNode[];
  } else {
    return null;
  }

  return (
    <div className="bn-viewer px-12 pb-24 max-w-4xl mx-auto">
      <BlockCtx.Provider value={{ pageById }}>
        {blocks.map((block, i) => (
          <Block key={block.id ?? i} node={block} />
        ))}
      </BlockCtx.Provider>
    </div>
  );
}

// Context so nested inline renderers can look up internal link targets without
// threading props through every block type.
interface BlockCtxValue {
  pageById: Map<string, TreeNodeResponse>;
}
const BlockCtx = React.createContext<BlockCtxValue>({ pageById: new Map() });

function Block({ node }: { node: BlockNode }) {
  const inline = renderInline((node.content as InlineContent[]) ?? []);
  const children = (node.children ?? []).map((c, i) => <Block key={c.id ?? i} node={c} />);

  switch (node.type) {
    case 'paragraph':
      return <p>{inline}{children}</p>;

    case 'heading': {
      const level = (node.props?.level as number) ?? 1;
      if (level === 1) return <h1>{inline}</h1>;
      if (level === 2) return <h2>{inline}</h2>;
      return <h3>{inline}</h3>;
    }

    case 'bulletListItem':
      return (
        <ul>
          <li>{inline}{children}</li>
        </ul>
      );

    case 'numberedListItem':
      return (
        <ol>
          <li>{inline}{children}</li>
        </ol>
      );

    case 'checkListItem': {
      const checked = (node.props?.checked as boolean) ?? false;
      return (
        <div className="flex gap-2 items-start">
          <input type="checkbox" checked={checked} readOnly className="mt-1.5" />
          <div>{inline}{children}</div>
        </div>
      );
    }

    case 'quote':
      return <blockquote>{inline}{children}</blockquote>;

    case 'toggle':
      // Notion toggles used to be collapsible. We deliberately render them as
      // a paragraph followed by their children inline — the user prefers
      // everything visible at once with no expand/collapse interaction.
      return (
        <>
          <p>{inline}</p>
          {children}
        </>
      );

    case 'toggleHeading': {
      // Same treatment for toggleable headings — render as a regular heading
      // and let the children flow below it in normal document order.
      const lvl = (node.props?.level as number) ?? 1;
      if (lvl === 1) return <><h1>{inline}</h1>{children}</>;
      if (lvl === 2) return <><h2>{inline}</h2>{children}</>;
      return <><h3>{inline}</h3>{children}</>;
    }

    case 'codeBlock':
    case 'code': {
      const text = (node.content as InlineContent[] | undefined)
        ?.map((c) => c.text ?? '')
        .join('') ?? '';
      return <pre><code>{text}</code></pre>;
    }

    case 'image': {
      const url = node.props?.url as string | undefined;
      const caption = node.props?.caption as string | undefined;
      if (!url) return null;
      return (
        <figure>
          <img src={url} alt={caption ?? ''} />
          {caption && <figcaption className="text-sm text-gray-500">{caption}</figcaption>}
        </figure>
      );
    }

    case 'video':
    case 'audio':
    case 'file': {
      const url = node.props?.url as string | undefined;
      if (!url) return null;
      if (node.type === 'video') return <video src={url} controls className="my-2 max-w-full" />;
      if (node.type === 'audio') return <audio src={url} controls className="my-2" />;
      return <a href={url} download className="underline">Download file</a>;
    }

    case 'bookmark': {
      const url = node.props?.url as string | undefined;
      if (!url) return null;
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block border border-brand-200 p-3 my-3 hover:bg-brand-50"
        >
          <div className="text-brand-700 font-medium truncate">{url}</div>
        </a>
      );
    }

    case 'embed': {
      const url = node.props?.url as string | undefined;
      if (!url) return null;
      return (
        <iframe
          src={url}
          className="w-full h-96 my-3 border border-brand-200"
          allowFullScreen
        />
      );
    }

    case 'loomEmbed': {
      const url = node.props?.url as string | undefined;
      if (!url) return null;
      // Loom share URLs (https://loom.com/share/<id>) need to be converted to
      // embed URLs (https://loom.com/embed/<id>) for iframe rendering. Embed
      // URLs that already use /embed/ pass through unchanged.
      const embedUrl = url.replace('/share/', '/embed/');
      return (
        <div className="my-3 aspect-video">
          <iframe
            src={embedUrl}
            className="w-full h-full border border-brand-200"
            allowFullScreen
            allow="autoplay; fullscreen"
          />
        </div>
      );
    }

    case 'columnList':
      return <div className="flex gap-4 my-3">{children}</div>;

    case 'column':
      return <div className="flex-1">{children}</div>;

    case 'table': {
      const rows = (node.content as unknown as { rows?: { cells: InlineContent[][] }[] })?.rows ?? [];
      return (
        <table className="border-collapse border border-brand-200 my-3">
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.cells.map((cell, j) => (
                  <td key={j} className="border border-brand-200 px-2 py-1">
                    {renderInline(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    default:
      // Unknown block — render children if any so we don't lose nested content
      return <div data-block-type={node.type}>{inline}{children}</div>;
  }
}

function InlineLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  const { pageById } = React.useContext(BlockCtx);
  const isInternal = href.startsWith('/p/') || href.startsWith('/');

  if (isInternal) {
    // Try to resolve a target page so we can use its real icon instead of the
    // generic 🔗. Only /p/<id> paths are looked up — other internal paths
    // (e.g. /media/...) fall back to a neutral dot.
    const match = href.match(/^\/p\/([^/?#]+)/);
    const targetId = match?.[1];
    const target = targetId ? pageById.get(targetId) : undefined;

    return (
      <Link
        to={href}
        className="text-brand-600 hover:underline inline-flex items-baseline gap-1"
      >
        {target ? (
          <PageIcon icon={target.icon} size="sm" alt="" fallback="dot" />
        ) : (
          <span className="text-brand-300" aria-hidden="true">·</span>
        )}
        <span>{children}</span>
      </Link>
    );
  }

  // External link — 🔗 prefix makes it visually distinct from internal links
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-brand-600 hover:underline"
    >
      <span aria-hidden="true">🔗 </span>
      {children}
    </a>
  );
}

function renderInline(content: InlineContent[]): React.ReactNode {
  if (!content) return null;
  return content.map((item, i) => {
    if (item.type === 'link') {
      return (
        <InlineLink key={i} href={item.href ?? ''}>
          {renderInline(item.content ?? [])}
        </InlineLink>
      );
    }
    if (item.type === 'pageLink') {
      // Custom inline content emitted by the editor's /page slash command.
      // Looks up the target page in the tree cache and renders a clickable
      // icon + title; falls back to a generic link card if the target is
      // missing or the tree hasn't loaded yet.
      const pageId = (item.props?.pageId as string | undefined) ?? '';
      return <PageLinkInline key={i} pageId={pageId} />;
    }
    if (item.type === 'text') {
      let node: React.ReactNode = item.text ?? '';
      const styles = item.styles ?? {};
      if (styles.bold) node = <strong>{node}</strong>;
      if (styles.italic) node = <em>{node}</em>;
      if (styles.underline) node = <u>{node}</u>;
      if (styles.strike) node = <s>{node}</s>;
      if (styles.code) node = <code>{node}</code>;
      return <React.Fragment key={i}>{node}</React.Fragment>;
    }
    return null;
  });
}

function PageLinkInline({ pageId }: { pageId: string }) {
  const { pageById } = React.useContext(BlockCtx);
  const target = pageId ? pageById.get(pageId) : undefined;
  const title = target?.title || 'Untitled';
  if (!pageId) {
    return <span className="text-brand-400 italic">(broken page link)</span>;
  }
  return (
    <Link
      to={`/p/${pageId}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 -mx-0.5 hover:bg-brand-50 text-brand-700 no-underline align-baseline"
    >
      {target ? (
        <PageIcon icon={target.icon} size="sm" alt="" fallback="dot" />
      ) : (
        <span className="text-brand-300" aria-hidden>·</span>
      )}
      <span className={target ? '' : 'text-brand-400 italic'}>{title}</span>
    </Link>
  );
}
